'use client'

import { create } from 'zustand'
import { getBillingInfo } from '@/lib/api'
import type { BillingInfo } from '@/lib/api'

interface BillingInfoState {
  billingInfo: BillingInfo | null
  isLoading: boolean
  error: Error | null
  /** Tracks the current in-flight request so concurrent callers can share one network call. */
  inFlight: Promise<void> | null
  /** Deduped fetch — returns the in-flight promise if one is already running. */
  fetch: () => Promise<void>
  /**
   * Force a fresh fetch — used after mutations (topup/cancel/resubscribe) where the
   * caller needs data captured *after* the mutation completed. Last-call-wins via an
   * internal counter so concurrent fetches don't apply stale results.
   */
  forceFetch: () => Promise<void>
  clear: () => void
}

/**
 * Shared store for billing info. All components that need billing data subscribe
 * here so the /billing endpoint is only hit once per page load (plus on focus),
 * regardless of how many consumers there are.
 */
export const useBillingInfoStore = create<BillingInfoState>((set, get) => {
  let counter = 0

  const startFetch = ({ showLoading = false }: { showLoading?: boolean } = {}): Promise<void> => {
    const id = ++counter
    const promise = (async () => {
      try {
        const info = await getBillingInfo()
        if (id !== counter) return
        set({ billingInfo: info, error: null, isLoading: false })
      } catch (err) {
        if (id !== counter) return
        set({
          error: err instanceof Error ? err : new Error('Failed to fetch billing info'),
          isLoading: false,
        })
      } finally {
        if (id === counter) set({ inFlight: null })
      }
    })()
    set({ inFlight: promise, ...(showLoading ? { isLoading: true } : {}) })
    return promise
  }

  return {
    billingInfo: null,
    isLoading: true,
    error: null,
    inFlight: null,

    fetch: () => get().inFlight ?? startFetch(),

    forceFetch: () => startFetch({ showLoading: true }),

    clear: () => {
      counter += 1
      set({ billingInfo: null, error: null, isLoading: false, inFlight: null })
    },
  }
})
